<script lang="ts">
  import type { StoresKey } from "~/stores";
  import { getStoresContext } from "~/stores";
  import { currentDayHours } from "~/theme/demo2025/stores/scOpeningHours";
  import { onMount } from "svelte";
  import { get } from "svelte/store";
  
  type Obj_i18n<T> = { [locale: string]: T };
  type Link = { text: string; href: string; outbound: boolean; target?: string | undefined; };
  type SocialMediaLink = { icon: any; text: string; url: string; target?: string; };

  export let request_store_key: StoresKey;
  export let companyName: string;
  export let streetAddress: string;
  export let postalCode: string;
  export let companyLogo;
  export let clockIcon;
  export let mapIcon;
  export let openingHoursUrl: string;
  export let visitUsUrl: string;
  export let footerColumns;
  export let href_home;
  export let currentLocale: string;
  export let textColor: string = "";
  export let invertIcons: boolean = false;
  export let seeHours: string = "";
  export let openingHours: string = "";
  export let visitUs: string = "";
  export let connectHeading: string = "";
  export let socialMediaLinks: SocialMediaLink[] = [];
  export let subFooterLinks: Array<{ text: string; href: string; target?: string }> = [];
  export let subFooterAlignment: string = "right";
  export let showSocialIcons: boolean = true;
  export let showSocialText: boolean = true;
  export let iconSize: string = "base";
  export let iconGrowthFactor: number = 1.0;
  export let showSubmenuTitles: boolean = true;
  export let socialMediaLayout: string = "horizontal";
  export let allHoursText: string = "";
  export let visitUsText: string = "";
  
  let _class = "";
  export { _class as class };
  
  const stores_context = getStoresContext(request_store_key);
  const { i18nOf, T, reverse_router } = stores_context;

  // Cache initial values to prevent re-renders during hydration
  let initialPropsSet = false;
  
  // Remove all reactive dependencies after mount
  let displayedOpeningHours = "";
  let memoizedSocialMediaLinks: SocialMediaLink[] = [];
  let computedFooterColumns: any[] = [];

  onMount(() => {
    // Set icon size class once
    iconSizeClass = computeIconSizeClass();
    
    // Wait for stores to be ready before caching
    setTimeout(() => {
      if (!initialPropsSet) {
        // Cache social media links
        memoizedSocialMediaLinks = [...socialMediaLinks];
        
        // Ensure reverse_router is available before computing footer columns
        const router = reverse_router.get();
        if (router && router.routePage) {
          computedFooterColumns = footerColumns.map((column: any) => ({
            title: column.title ? getLocalizedValue(column.title) : null,
            links: column.links ? column.links.map((item: any) => ({
              href: item.link ? router.routePage(item.link) : item.url,
              text: getLocalizedValue(item.name_i18n),
              target: item.link ? "_self" : "_blank"
            })) : []
          }));
        } else {
          console.error('Footer: reverse_router not available or missing routePage method', { router, reverse_router });
          computedFooterColumns = [];
        }
        
        displayedOpeningHours = openingHours || get(currentDayHours);
        initialPropsSet = true;
      }
    }, 100); // Small delay to ensure stores are ready
    
    // Subscribe to currentDayHours changes but debounce them
    const unsubscribe = currentDayHours.subscribe((value: string) => {
      if (!openingHours && value && value !== displayedOpeningHours && initialPropsSet) {
        displayedOpeningHours = value;
      }
    });
    
    return unsubscribe;
  });

  function getLocalizedValue<T>(i18nObject: Obj_i18n<T> | undefined): string {
    if (i18nObject && i18nObject[currentLocale]) {
      return i18nObject[currentLocale] as string;
    }
    return (i18nObject ? i18nOf(i18nObject) : '') as string || '';
  }

  // Define icon size CSS class based on selected size
  let iconSizeClass = '';
  
  function computeIconSizeClass() {
    switch (iconSize) {
      case 'small':
        return 'icon-size-sm';
      case 'large':
        return 'icon-size-lg';
      default:
        return 'icon-size-base';
    }
  }
</script>

<footer>
  <div class="footer-container {_class} {invertIcons ? 'invert-icons' : ''} {iconSizeClass} social-layout-{socialMediaLayout}" style={textColor ? `--footer-text-color: ${textColor}; --icon-growth-factor: ${iconGrowthFactor}` : `--icon-growth-factor: ${iconGrowthFactor}`}>
    <div class="footer" id="footer-content">
      {#if companyLogo}
        <div class="mobile-logo-container">
          <a href={href_home}>
            <img
              src={companyLogo.src}
              alt="brand logo"
              class="mobile-brand-logo logo" />
          </a>
        </div>
      {/if}

      <div class="footer-info">
        <div class="footer-col1">
          <div class="openinghours">
            {#if clockIcon}
              <img src={clockIcon.src} alt="clock Icon" class="icon" />
            {/if}
            <div>
              <h2>
                {#if openingHoursUrl}
                  <a href={openingHoursUrl} class="hours-link">{seeHours || allHoursText}</a>
                {:else}
                  {seeHours || allHoursText}
                {/if}
              </h2>
              <p id="openinghour-info">{displayedOpeningHours}</p>
            </div>
          </div>
          <div class="address">
            {#if mapIcon}
              <img src={mapIcon.src} alt="map Icon" class="icon" />
            {/if}
            <div>
              <h2>
                {#if visitUsUrl}
                  <a href={visitUsUrl} class="visit-us-link">{visitUs || visitUsText}</a>
                {:else}
                  {visitUs || visitUsText}
                {/if}
              </h2>
              <p id="address-info">{companyName}</p>
              <p id="address-info">{streetAddress}</p>
              <p id="address-info">{postalCode}</p>
            </div>
          </div>
        </div>

        <div class="footer-col2">
          {#each computedFooterColumns as column}
            <div class="footer-column">
              {#if column.title}
                <h2 class="column-title">{column.title}</h2>
              {/if}
              {#if column.links && column.links.length > 0}
                {#each column.links as item}
                  <div class="footer-link">
                    <a
                      href={item.href}
                      target={item.target}>
                      {item.text}
                    </a>
                  </div>
                {/each}
              {/if}
            </div>
          {/each}

          <div class="footer-connect">
            <div class="social-links-container">
              {#each memoizedSocialMediaLinks as socialLink, index (socialLink.url + socialLink.text)}
                <div class="social-link">
                  <a href={socialLink.url} target={socialLink.target || "_blank"}>
                    {#if showSocialIcons && socialLink.icon && socialLink.icon.src}
                      <img 
                        src={socialLink.icon.src} 
                        alt="Social media icon" 
                        class="social-icon"
                        data-footer-icon={index} />
                    {/if}
                    {#if showSocialText}
                      <span>{socialLink.text || 'Social Media'}</span>
                    {/if}
                  </a>
                </div>
              {/each}
            </div>
          </div>
        </div>
      </div>

      <div class="footer-logo-banner subfooter-alignment-{subFooterAlignment}">
        {#if companyLogo}
          <div class="logo-container">
            <a href={href_home}>
              <img src={companyLogo.src} alt="brand logo" class="brand-logo logo" />
            </a>
          </div>
        {/if}
        {#if subFooterLinks.length > 0}
          <div class="footer-text">
            {#each subFooterLinks as link}
              <div class="subfooter-link">
                <a href={link.href} target={link.target}>{link.text}</a>
              </div>
            {/each}
          </div>
        {/if}
      </div>
    </div>
  </div>
</footer>

<style lang="scss">
  /* Root Variables */
  .footer-container {
    --footer-padding: clamp(1.5rem, 5vw, 3rem);
    --footer-gap: clamp(0.5rem, 2vw, 2rem);
    --column-gap: clamp(1rem, 3vw, 2.5rem);
    --text-color: var(--footer-text-color, inherit);
    --icon-size: clamp(18px, 1.25rem, 24px);
    --icon-base-size: 24px;
    --icon-sm-scale: 0.75;  /* 75% of base size = 18px */
    --icon-lg-scale: 1.33;  /* 133% of base size = 32px */
    --icon-growth-factor: 1.0;  /* Additional multiplier for icon sizes */
    --social-icon-size: var(--icon-base-size);
    --logo-height: clamp(30px, 2.5rem, 40px);
    --separator-color: #e5e5e5;
    
    /* Base styles */
    width: 100%;
    padding: 3rem 0;
    margin: 0 auto;
    color: var(--text-color);
    
    /* Icon size classes - Social media icons only */
    &.icon-size-sm {
      --social-icon-size: calc(var(--icon-base-size) * var(--icon-sm-scale) * var(--icon-growth-factor));
    }
    
    &.icon-size-base {
      --social-icon-size: calc(var(--icon-base-size) * var(--icon-growth-factor));
    }
    
    &.icon-size-lg {
      --social-icon-size: calc(var(--icon-base-size) * var(--icon-lg-scale) * var(--icon-growth-factor));
    }
  }

  /* Layout components - ordered by document flow */
  .footer {
    display: grid;
    grid-template-rows: 1fr auto;
    width: 100%;
    gap: var(--footer-gap);
  }
  
   h2 {
    font-weight: 600;
    font-size: 20px;
    line-height: 24px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    
    .hours-link,
    .visit-us-link {
      color: inherit;
      text-decoration: none;
      cursor: pointer;
      
      &:hover {
        text-decoration: none;
      }
    }
  }

  .footer-info {
    display: flex;
    width: 100%;
    justify-content: space-between;
    gap: var(--column-gap);
  }

  .footer-col1 {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    min-width: 250px;
    max-width: 40%;
    gap: var(--footer-gap);
  }

  .footer-col2 {
    flex: 1 1 auto;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: var(--footer-gap);
  }
  
  .footer-column {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    margin-left: var(--footer-gap);
  }

  .footer-connect {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    margin-left: var(--footer-gap);
  }

  .footer-logo-banner {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    padding-top: 1.25rem;
    margin-top: 1rem;
    border-top: 1px solid var(--separator-color);
    
    &.subfooter-alignment-left {
      justify-content: flex-start;
      
      .footer-text {
        margin-left: 2rem;
      }
    }
    
    &.subfooter-alignment-center {
      justify-content: center;
      flex-direction: column;
      
      .logo-container {
        margin-bottom: 1rem;
      }
      
      .footer-text {
        display: flex;
        justify-content: center;
        gap: 1.5rem;
      }
    }
    
    &.subfooter-alignment-right {
      justify-content: space-between;
      
      .footer-text {
        display: flex;
        justify-content: flex-end;
        gap: 1.5rem;
      }
    }
    
    &.subfooter-alignment-space-between {
      justify-content: space-between;
      
      .footer-text {
        display: flex;
        justify-content: flex-end;
        gap: 1.5rem;
      }
    }
  }

  /* Content components */
  .openinghours {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 1rem;
    margin-bottom: 1.25rem;
  }

  .address {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 1rem;
  }

  .social-link {
    display: flex;
    margin-bottom: 0.5rem;
  }

  .social-links-container {
    display: flex;
    flex-direction: column;
  }

  /* Typography */
  h2 {
    margin-bottom: 0.75rem;
    font-size: clamp(16px, 1.25rem, 20px);
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: var(--text-color);
  }

  .address-info,
  .openinghour-info,
  .footer-link,
  .footer-text,
  p {
    font-weight: 500;
    color: var(--text-color);
  }
  
  .footer-link {
    margin-bottom: 0.5rem;
  }

  #openinghour-info {
    margin-top: 0.25rem;
  }
  
  /* Links */
  a {
    text-decoration: none;
    color: var(--text-color);
  }

  a:hover {
    color: var(--text-color);
  }

  .social-link a {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1rem;
    font-weight: 500;
  }

  /* Images */
  img {
    height: var(--icon-size);
    width: var(--icon-size);
    vertical-align: top;
  }

  .brand-logo {
    height: var(--logo-height);
    width: auto;
  }

  .social-icon {
    height: var(--social-icon-size);
    width: var(--social-icon-size);
    display: inline-block;
    vertical-align: middle;
  }

  /* Utility classes */
  .invert-icons .icon,
  .invert-icons .logo,
  .invert-icons .social-icon {
    filter: invert(1) brightness(100%);
  }

  .mobile-logo-container {
    display: none;
  }

  .footer-text {
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
  }
  
  .subfooter-link {
    font-weight: 500;
    font-size: 0.9rem;
  }

  /* Media queries */
  @media (max-width: 1024px) {
    .footer-container {
      --footer-padding: clamp(1rem, 3vw, 1.5rem);
      --footer-gap: clamp(0.5rem, 2vw, 1.5rem);
    }
  }

  @media (max-width: 768px) {
    /* Container adjustments */
    .footer-container {
      padding: var(--footer-padding);
    }

    /* Layout changes */
    .footer {
      display: flex;
      flex-direction: column;
      gap: 2rem;
    }

    .footer-info {
      flex-direction: column;
      order: 2;
    }

    .footer-col1 {
      width: 100%;
      max-width: 100%;
      order: 2;
    }

    .footer-col2 {
      width: 100%;
      order: 1;
      justify-content: center;
      flex-direction: column;
      align-items: center;
      margin-bottom: 1.5rem;
    }

    .footer-col2::after {
      content: "";
      display: block;
      width: 100%;
      height: 1px;
      background-color: var(--separator-color);
      margin: 1.5rem 0 0.5rem;
    }

    .footer-logo-banner {
      order: 3;
      
      &.subfooter-alignment-left,
      &.subfooter-alignment-center,
      &.subfooter-alignment-right,
      &.subfooter-alignment-space-between {
        flex-direction: column;
        justify-content: center;
        
        .footer-text {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          margin-left: 0;
          margin-top: 1rem;
        }
      }
    }

    .subfooter-link {
      text-align: center;
    }

    /* Component adjustments */
    .logo-container {
      display: none;
    }

    .mobile-logo-container {
      display: block;
      text-align: center;
      order: 1;
      margin: 1.5rem 0;
    }

    .mobile-brand-logo {
      height: var(--logo-height);
      width: auto;
    }

    .footer-column,
    .footer-connect {
      width: auto;
      margin-left: 0;
      text-align: center;
      align-items: center;
      margin-bottom: 1rem;
    }

    .footer-connect {
      align-items: flex-start;
    }

    .footer-text {
      width: 100%;
      text-align: center;
      margin: 0;
    }

    /* Text alignment */
    .footer-link,
    .column-title,
    .connect {
      text-align: center;
    }

    /* Grid adjustments */
    .openinghours,
    .address {
      justify-content: center;
      text-align: center;
      grid-template-columns: 1fr;
      gap: 0.5rem;
    }

    .openinghours img,
    .address img {
      display: block;
      margin: 0 auto 0.5rem;
    }

    /* Social media layout options for mobile */
    .social-layout-horizontal .social-links-container {
      flex-direction: row;
      flex-wrap: wrap;
      gap: 1rem;
      
      .social-link {
        margin-bottom: 0;
      }
    }
    
    .social-layout-vertical .social-links-container {
      flex-direction: column;
      
      .social-link {
        margin-bottom: 0.75rem;
      }
    }
  }
</style>
